import React, { memo } from "react";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";

import {
  WarningIcon,
  PreciseIcon,
  LeafIcon,
  ReturnIcon,
  DepartIcon,
} from "../../../icons";
import Loading from "./Loading";
import TotalDataRow from "./TotalDataRow";
import ExceptionsDataRow from "./ExceptionsDataRow";
import BaseCard from "./BaseCard";
import { formatTimeStringWithDefaultMaxValue } from "../../../../utils/date";
import { View, Text } from "../../../Base";
import { VEHICLE } from "../../../../constants/strings";
import palette from "../../../styles/palette";

const useStyles = makeStyles(() => ({
  row: {
    display: "flex",
    flexDirection: "row",
    marginTop: "0.313rem",
    justifyContent: "space-between",
  },
  vehicleRow: {
    justifyContent: "flex-start",
  },
  text: {
    fontSize: "0.75rem",
    lineHeight: "1rem",
    color: "white",
    flexDirection: "row",
    display: "flex",
  },
  lightGrey: {
    color: palette.typography.white.high,
  },
  grey: {
    color: palette.typography.white.medium,
  },
  valueMargin: {
    marginLeft: "0.313rem",
  },
  textContainer: {
    display: "flex",
    flexDirection: "row",
  },
  icon: {
    width: "0.75rem",
    height: "0.75rem",
    marginLeft: "0.125rem",

    "& svg": {
      width: "0.75rem",
      height: "0.75rem",
      fill: palette.typography.white.medium,
    },
  },
  leafIcon: {
    width: "1rem",
    height: "1rem",
    marginLeft: "0.125rem",
    marginTop: "-0.25rem",

    "& svg": {
      width: "1.5rem",
      height: "1.5rem",
    },
  },
  iconDefault: {
    width: "0.75rem",
    height: "0.75rem",
    lineHeight: "1rem",

    "& svg": {
      width: "0.75rem",
      height: "0.75rem",
    },
  },
  bold: {
    fontWeight: "bold",
  },
}));

const TimeRow = memo(({ route }) => {
  const styles = useStyles();

  return (
    <View className={styles.row}>
      <View className={styles.textContainer}>
        <Text
          className={clsx(styles.text, styles.lightGrey)}
          data-testid="duty-time"
        >
          {`${formatTimeStringWithDefaultMaxValue(
            route.dutyStartTime,
            "00:00"
          )} - ${formatTimeStringWithDefaultMaxValue(
            route.dutyEndTime,
            "00:00"
          )}`}
        </Text>
      </View>
      <View className={styles.textContainer}>
        <Text
          className={clsx(styles.text, styles.lightGrey)}
          data-testid="depart-time"
        >
          {formatTimeStringWithDefaultMaxValue(route.departTime, "00:00")}
        </Text>
        <View className={styles.icon}>
          <DepartIcon />
        </View>
      </View>
      <View className={styles.textContainer}>
        <Text
          className={clsx(styles.text, styles.lightGrey)}
          data-testid="working-hours"
        >
          {formatTimeStringWithDefaultMaxValue(route.totalDutyTime, "00:00")}
        </Text>
        <View className={styles.icon}>
          <PreciseIcon />
        </View>
      </View>
      <View className={styles.textContainer}>
        <Text
          className={clsx(styles.text, styles.lightGrey)}
          data-testid="return-time"
        >
          {formatTimeStringWithDefaultMaxValue(route.returnTime, "00:00")}
        </Text>
        <View className={styles.icon}>
          <ReturnIcon />
        </View>
      </View>
    </View>
  );
});

const VehicleRow = memo(({ route }) => {
  const styles = useStyles();

  return (
    <View className={clsx(styles.row, styles.vehicleRow)}>
      <Text className={clsx(styles.text, styles.bold)} data-testid="vehicle">
        {VEHICLE}
      </Text>
      <Text
        className={clsx(styles.text, styles.valueMargin, styles.grey)}
        data-testid="vehicle-value"
      >
        {route.vehicleType}
      </Text>
      <View className={styles.leafIcon}>
        {route.electricVehicle && <LeafIcon data-testid="vehicle-icon-leaf" />}
      </View>
    </View>
  );
});

const WeightAndLoadingRow = memo(({ route }) => {
  const styles = useStyles();

  return (
    <View className={styles.row}>
      <View className={styles.textContainer}>
        <Text className={clsx(styles.text, styles.bold)} data-testid="weight">
          Weight
        </Text>
        <Text
          className={clsx(styles.text, styles.valueMargin, styles.lightGrey)}
          data-testid="weight-value"
        >
          {route.totalWeight + " Kg"}
        </Text>
        {route.vehicleOverweight && (
          <View className={styles.iconDefault}>
            <WarningIcon />
          </View>
        )}
      </View>
      <Loading
        routeStatusDescription={route.routeStatusDescription}
        loadingPercentage={route.loadingPercentage}
        lowLoadRate={route.lowLoadRate}
        routeStatus={route.routeStatus}
      />
    </View>
  );
});

const DetailedCard = ({
  route,
  spot,
  active,
  onDoubleClick,
  onClick,
  onRouteCodeClick,
  onStatusChange,
}) => {
  return (
    <BaseCard
      route={route}
      spot={spot}
      active={active}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onRouteCodeClick={onRouteCodeClick}
      onStatusChange={onStatusChange}
      detailedStatus
    >
      <TimeRow route={route} />
      <TotalDataRow route={route} />
      <ExceptionsDataRow route={route} />
      <VehicleRow route={route} />
      <WeightAndLoadingRow route={route} />
    </BaseCard>
  );
};

export default memo(DetailedCard);
